import { useCallback, useEffect, useMemo, useState } from "react"

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max))

interface UseGalleryNavigationParams {
  itemsLength: number
  initialIndex?: number
  wrapNavigation?: boolean
}

const useGalleryNavigation = ({ itemsLength, initialIndex = 0, wrapNavigation = true }: UseGalleryNavigationParams) => {
  const maxIndex = Math.max(0, itemsLength - 1)
  const safeInitialIndex = clamp(initialIndex, 0, maxIndex)

  const [currentIndex, setCurrentIndex] = useState<number>(safeInitialIndex)

  useEffect(() => {
    setCurrentIndex(prev => clamp(prev, 0, maxIndex))
  }, [maxIndex])

  useEffect(() => {
    setCurrentIndex(safeInitialIndex)
  }, [safeInitialIndex])

  const canNavigate = itemsLength > 1

  const goToIndex = useCallback(
    (nextIndex: number) => {
      setCurrentIndex(clamp(nextIndex, 0, maxIndex))
    },
    [maxIndex]
  )

  const goToPrev = useCallback(() => {
    if (!canNavigate) return
    setCurrentIndex(prev => {
      if (wrapNavigation) return prev === 0 ? maxIndex : prev - 1
      return clamp(prev - 1, 0, maxIndex)
    })
  }, [canNavigate, maxIndex, wrapNavigation])

  const goToNext = useCallback(() => {
    if (!canNavigate) return
    setCurrentIndex(prev => {
      if (wrapNavigation) return prev === maxIndex ? 0 : prev + 1
      return clamp(prev + 1, 0, maxIndex)
    })
  }, [canNavigate, maxIndex, wrapNavigation])

  const isPrevDisabled = useMemo(
    () => !canNavigate || (!wrapNavigation && currentIndex === 0),
    [canNavigate, wrapNavigation, currentIndex]
  )

  const isNextDisabled = useMemo(
    () => !canNavigate || (!wrapNavigation && currentIndex === maxIndex),
    [canNavigate, wrapNavigation, currentIndex, maxIndex]
  )

  return {
    currentIndex,
    goToIndex,
    goToPrev,
    goToNext,
    isPrevDisabled,
    isNextDisabled,
    canNavigate
  }
}

export { useGalleryNavigation }
